import { useEffect, useReducer } from "react";

// Module-level cache so each audio file is decoded only once, shared by all clips.
const peaksCache = new Map<string, number[] | "loading" | "error">();
const peaksSubscribers = new Map<string, Set<() => void>>();
let sharedAudioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!sharedAudioCtx) {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    sharedAudioCtx = new Ctor();
  }
  return sharedAudioCtx;
}

function computePeaks(buffer: AudioBuffer, buckets: number): number[] {
  const data = buffer.getChannelData(0);
  const blockSize = Math.max(1, Math.floor(data.length / buckets));
  const peaks: number[] = [];
  for (let i = 0; i < buckets; i++) {
    const start = i * blockSize;
    let max = 0;
    for (let j = 0; j < blockSize && start + j < data.length; j++) {
      const v = Math.abs(data[start + j]);
      if (v > max) max = v;
    }
    peaks.push(max);
  }
  const peak = peaks.reduce((m, v) => Math.max(m, v), 0) || 1;
  return peaks.map((v) => v / peak);
}

async function loadPeaks(url: string) {
  if (peaksCache.has(url)) return;
  peaksCache.set(url, "loading");
  try {
    const resp = await fetch(url);
    const buf = await resp.arrayBuffer();
    const ctx = getAudioCtx();
    if (!ctx) {
      peaksCache.set(url, "error");
    } else {
      const audioBuf = await ctx.decodeAudioData(buf);
      peaksCache.set(url, computePeaks(audioBuf, 1024));
    }
  } catch {
    peaksCache.set(url, "error");
  }
  peaksSubscribers.get(url)?.forEach((cb) => cb());
}

export function useAudioPeaks(url?: string): number[] | null {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    if (!url) return;
    if (peaksCache.get(url) === undefined) loadPeaks(url);
    let set = peaksSubscribers.get(url);
    if (!set) {
      set = new Set();
      peaksSubscribers.set(url, set);
    }
    set.add(force);
    return () => {
      set?.delete(force);
    };
  }, [url]);
  if (!url) return null;
  const v = peaksCache.get(url);
  return Array.isArray(v) ? v : null;
}

export function Waveform({
  widthPx,
  color,
  url,
  startFrac = 0,
  endFrac = 1,
}: {
  widthPx: number;
  color: string;
  url?: string;
  startFrac?: number;
  endFrac?: number;
}) {
  const peaks = useAudioPeaks(url);
  const BAR_W = 2;
  const GAP = 1.5;
  const count = Math.max(0, Math.floor(widthPx / (BAR_W + GAP)));

  let bars: number[];
  if (peaks && peaks.length > 0) {
    const lo = Math.max(0, Math.min(1, startFrac));
    const hi = Math.max(lo, Math.min(1, endFrac));
    const from = Math.floor(lo * peaks.length);
    const to = Math.max(from + 1, Math.floor(hi * peaks.length));
    const slice = peaks.slice(from, to);
    bars = [];
    for (let i = 0; i < count; i++) {
      const idx = Math.floor((i / Math.max(count, 1)) * slice.length);
      bars.push(0.12 + (slice[idx] ?? 0) * 0.88);
    }
  } else {
    // loading / unavailable → subtle flat baseline
    bars = new Array(count).fill(0.12);
  }

  return (
    <div
      className="absolute bottom-0 left-0 right-0 flex items-end gap-[1.5px] px-1 pointer-events-none"
      style={{ height: "40%" }}
    >
      {bars.map((ratio, i) => (
        <div
          key={i}
          style={{
            width: BAR_W,
            height: `${ratio * 100}%`,
            backgroundColor: color,
            opacity: 0.3,
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}
